import { deviation, max, mean } from 'd3-array';
import { isNil } from 'lodash';

import type { CompositionV2DataQuery } from '@revelio/data-access';

import type { SecondaryDimensionValues } from '../../PageConfigProvider/types/dimension';
import type { CompositionMetricValues } from '../../PageConfigProvider/types/metric';
import {
  isEmptyCompositionBucket,
  isOtherCompositionBucket,
  OTHER_BUCKET_LABEL,
} from '../composition/composition-v3/is-empty-composition-bucket';
import {
  type BreakdownData,
  type BreakdownSingleDataSet,
  type BreakdownTimeseriesDataSet,
  type DataProviderSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
  type TimeseriesDataPoint,
  type TimeseriesDataSet,
} from '../types';

const NUM_OF_TOP_SEGMENTS = 7;

const v2BreakdownLabel = (
  rowId: number | null | undefined,
  shortName: string | null | undefined
): string | undefined => {
  if (isOtherCompositionBucket(rowId)) return OTHER_BUCKET_LABEL;
  return shortName ?? undefined;
};

export const convertCompositionData = ({
  metric,
  segment,
  data,
  isTimeseries,
  exchangeRate = 1,
}: {
  metric: CompositionMetricValues;
  segment?: SecondaryDimensionValues;
  data: NonNullable<CompositionV2DataQuery['composition_v2']>;
  isTimeseries?: boolean;
  exchangeRate?: number;
}): DataProviderSet => {
  if (segment === 'timeseries') {
    const compositionData: TimeseriesDataSet['data'] = data?.entities?.map(
      (entity) => {
        const metadata = entity.metadata;
        const dim = metadata?.short_name;

        const dateRefs = data.refs.months;

        const metricData = entity.metrics?.[metric];
        const timeseries =
          metricData?.map((metricDataPoint): TimeseriesDataPoint => {
            const rawValue = metricDataPoint.value ?? null;
            const convertedValue =
              typeof rawValue === 'number' && metric === 'salary'
                ? rawValue * exchangeRate
                : rawValue;

            return {
              date: dateRefs[metricDataPoint.id] ?? '',
              value: convertedValue,
            };
          }) ?? [];

        return {
          ...(metadata?.type === 'DATA_KIND_COMPANY'
            ? { id: String(metadata?.id) }
            : {}),
          dim,
          timeseries,
        };
      }
    );

    return { type: DataType.TIMESERIES, data: compositionData };
  }

  /** ======== Stacked Bar & Grouped Bar & Line Chart ======== */
  if (segment && segment !== 'company' && segment !== 'competitors') {
    const hasTimeseriesData = data?.entities?.some((entity) => {
      const metricData = entity.metrics?.[segment];
      return metricData?.some(
        (metricDataPoint) =>
          metricDataPoint.timeseries && metricDataPoint.timeseries.length > 0
      );
    });

    // Only convert to BREAKDOWN_TIMESERIES when explicitly in timeseries mode
    // Snapshot views should use BREAKDOWN_SINGLE even if timeseries data exists
    if (hasTimeseriesData && isTimeseries) {
      const segmentTimeseriesMap: Record<
        string,
        { id?: string; dim: string; timeseries: TimeseriesDataPoint[] }
      > = {};

      data?.entities?.forEach((entity) => {
        const metricData = entity.metrics?.[segment];

        const selectedMetrics =
          metricData?.filter(
            (metricDataPoint) =>
              !isEmptyCompositionBucket(metricDataPoint?.metadata?.id)
          ) ?? [];

        selectedMetrics.forEach((metric) => {
          const segmentName = v2BreakdownLabel(
            metric.metadata?.id,
            metric.metadata?.short_name
          );
          if (!segmentName) return;

          if (!segmentTimeseriesMap[segmentName]) {
            segmentTimeseriesMap[segmentName] = {
              id:
                entity.metadata?.type === 'DATA_KIND_COMPANY'
                  ? String(entity.metadata?.id)
                  : '',
              dim: segmentName,
              timeseries: [],
            };
          }

          const timeseriesLength = metric.timeseries?.length ?? 0;
          const dateRefs = data.refs.months;

          for (
            let timePointIndex = 0;
            timePointIndex < timeseriesLength;
            timePointIndex++
          ) {
            const timePoint = metric.timeseries?.[timePointIndex];
            if (!timePoint) continue;

            const timePointId = timePoint.id;
            const date = dateRefs[timePointId];
            if (!date) continue;

            const existingPoint =
              segmentTimeseriesMap[segmentName].timeseries[timePointIndex];
            if (existingPoint) {
              existingPoint.value =
                (existingPoint.value ?? 0) + (timePoint.count ?? 0);
            } else {
              segmentTimeseriesMap[segmentName].timeseries[timePointIndex] = {
                date,
                value: timePoint.count ?? 0,
              };
            }
          }
        });
      });

      const totalCountByTimePointIndex: number[] = [];
      Object.values(segmentTimeseriesMap).forEach((segmentSeries) => {
        segmentSeries.timeseries.forEach((point, timePointIndex) => {
          const count = typeof point.value === 'number' ? point.value : 0;
          totalCountByTimePointIndex[timePointIndex] =
            (totalCountByTimePointIndex[timePointIndex] ?? 0) + count;
        });
      });

      const compositionData: BreakdownTimeseriesDataSet['data'] = Object.values(
        segmentTimeseriesMap
      ).map(
        (item): BreakdownData<TimeseriesDataPoint> => ({
          dim: item.dim,
          breakdown: item.timeseries.map((point, timePointIndex) => {
            const count = typeof point.value === 'number' ? point.value : 0;
            const total = totalCountByTimePointIndex[timePointIndex] ?? 0;
            const share = total > 0 ? count / total : null;

            return {
              ...point,
              share,
            };
          }),
        })
      );

      return { type: DataType.BREAKDOWN_TIMESERIES, data: compositionData };
    }

    const compositionData: BreakdownSingleDataSet['data'] = data?.entities?.map(
      (entity) => {
        const metadata = entity.metadata;
        const metricData = entity.metrics?.[segment];

        const breakdown = metricData
          ?.filter(
            (metricDataPoint) =>
              !isEmptyCompositionBucket(metricDataPoint?.metadata?.id)
          )
          ?.map((metricDataPoint): SingleDataPoint => {
            const timeseries = metricDataPoint.timeseries;
            const lastTimeseries = timeseries?.[timeseries.length - 1];

            return {
              dim:
                v2BreakdownLabel(
                  metricDataPoint.metadata?.id,
                  metricDataPoint.metadata?.short_name
                ) ?? metricDataPoint.metadata?.short_name,
              value: lastTimeseries?.count ?? null,
              share: lastTimeseries?.share ?? null,
              id:
                metricDataPoint.metadata?.id != null
                  ? String(metricDataPoint.metadata.id)
                  : null,
            };
          });

        return {
          ...(metadata?.type === 'DATA_KIND_COMPANY'
            ? { id: String(metadata?.id) }
            : {}),
          dim: metadata?.short_name,
          breakdown,
        };
      }
    );

    const limitedCompositionData = (() => {
      if (segment === 'skills') {
        const skillShareLookup: Record<string, number[]> = {};
        compositionData.forEach((entity) => {
          const skillsBreakdown = entity.breakdown;
          const sumOfSkills = skillsBreakdown.reduce(
            (acc, val) => (val.value ?? 0) + acc,
            0
          );

          skillsBreakdown.forEach((skill) => {
            skillShareLookup[skill.dim] = [
              ...(skillShareLookup[skill.dim] ?? []),
              (skill.value ?? 0) / sumOfSkills,
            ];
          });
        });

        const normalizedSkillsSorted = (() => {
          if (compositionData.length < 2) {
            return Object.entries(skillShareLookup)
              .map(([id, shares]): { id: string; normalizedMax: number } => ({
                id,
                normalizedMax: shares[0] ?? 0,
              }))
              .sort((a, b) => b.normalizedMax - a.normalizedMax);
          }

          return Object.entries(skillShareLookup)
            .map(([id, shares]): { id: string; normalizedMax: number } => {
              const shareStd = deviation(shares);
              const shareMax = max(shares);
              const shareMean = mean(shares);

              if (isNil(shareStd) || isNil(shareMax) || isNil(shareMean)) {
                return { id, normalizedMax: 0 };
              }
              return { id, normalizedMax: shareStd + shareMax - shareMean };
            })
            .sort((a, b) => b.normalizedMax - a.normalizedMax);
        })();

        const topSkills = normalizedSkillsSorted
          .slice(0, NUM_OF_TOP_SEGMENTS)
          .map((skill) => skill.id);

        return compositionData.map((entity) => {
          const filteredSortedValues = entity.breakdown
            .filter((value) => topSkills.includes(value.dim))
            .sort(
              (a, b) =>
                topSkills.indexOf(`${a.dim}`) - topSkills.indexOf(`${b.dim}`)
            );

          return {
            dim: entity.dim,
            breakdown: filteredSortedValues,
          };
        });
      }

      if (['geography', 'role', 'rics'].includes(segment)) {
        const segmentTotals: Record<string, number> = {};
        compositionData.forEach((entity) => {
          entity.breakdown.forEach((b) => {
            segmentTotals[b.dim] = (segmentTotals[b.dim] ?? 0) + (b.value ?? 0);
          });
        });

        const segmentDims = Object.keys(segmentTotals);
        if (segmentDims.length <= NUM_OF_TOP_SEGMENTS) {
          return compositionData;
        }

        const topSegments = Object.entries(segmentTotals)
          .sort(([, a], [, b]) => b - a)
          .slice(0, NUM_OF_TOP_SEGMENTS)
          .map(([dim]) => dim);

        return compositionData.map((entity) => ({
          ...entity,
          breakdown: entity.breakdown.filter((b) =>
            topSegments.includes(b.dim)
          ),
        }));
      }

      return compositionData;
    })();

    return { type: DataType.BREAKDOWN_SINGLE, data: limitedCompositionData };
  }

  const compositionData: SingleDataSet['data'] = data?.entities?.map(
    (entity): SingleDataPoint => {
      const metadata = entity.metadata;
      const dim = metadata?.short_name;

      const metricData = entity.metrics?.[metric];
      const lastMetricData = metricData?.[metricData?.length - 1];
      const rawValue = lastMetricData?.value ?? null;
      const value =
        typeof rawValue === 'number' && metric === 'salary'
          ? rawValue * exchangeRate
          : rawValue;

      return {
        // if entity is a company, use the rcid as the id, otherwise lookup the full
        // nested id for geo and role
        ...(metadata?.type === 'DATA_KIND_COMPANY'
          ? { id: String(metadata?.id) }
          : {}),
        dim,
        value,
      };
    }
  );

  const sortedCompositionData = (() => {
    if (segment && segmentsToSort.includes(segment)) {
      return compositionData.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    }
    return compositionData;
  })();

  return { type: DataType.SINGLE, data: sortedCompositionData };
};

const segmentsToSort: SecondaryDimensionValues[] = [
  'company',
  'geography',
  'rics',
  'role',
  'skills',
  'competitors',
];
export const convertCompositionTopNHeadcountData = ({
  segment,
  isGrouped,
  data,
}: {
  segment: Exclude<
    SecondaryDimensionValues,
    'company' | 'timeseries' | 'competitors'
  >;
  isGrouped?: boolean;
  data: NonNullable<CompositionV2DataQuery['composition_v2']>;
}): SingleDataSet | BreakdownSingleDataSet => {
  if (isGrouped) {
    const compositionData: BreakdownSingleDataSet['data'] = data?.entities?.map(
      (entity): BreakdownData<SingleDataPoint> => ({
        dim: entity.metadata?.short_name,
        breakdown:
          entity.metrics?.[segment]
            ?.filter(
              (segment) => !isEmptyCompositionBucket(segment.metadata?.id)
            )
            ?.map((segment) => ({
              dim:
                v2BreakdownLabel(
                  segment.metadata?.id,
                  segment.metadata?.short_name
                ) ?? segment.metadata?.short_name,
              value:
                segment.timeseries?.[segment.timeseries.length - 1]?.count ??
                null,
              share:
                segment.timeseries?.[segment.timeseries.length - 1]?.share ??
                null,
            })) ?? [],
      })
    );

    return { type: DataType.BREAKDOWN_SINGLE, data: compositionData };
  }

  const compositionDataObject = data?.entities?.reduce<
    Record<string, SingleDataPoint>
  >((acc, entity) => {
    const breakdownData = entity?.metrics?.[segment];

    breakdownData
      ?.filter((segment) => !isEmptyCompositionBucket(segment?.metadata?.id))
      ?.forEach((segment) => {
        const dim =
          v2BreakdownLabel(
            segment.metadata?.id,
            segment.metadata?.short_name
          ) ?? segment.metadata?.short_name;
        const lastTimeseries =
          segment.timeseries?.[segment.timeseries.length - 1];

        const value = lastTimeseries?.count ?? null;

        if (!acc[dim]) acc[dim] = { dim, value: value ?? 0 };
        else acc[dim].value = (acc[dim].value ?? 0) + (value ?? 0);
      });

    return acc;
  }, {});

  const compositionData: SingleDataSet['data'] = Object.values(
    compositionDataObject
  );
  const sortedCompositionData = (() => {
    if (segmentsToSort.includes(segment)) {
      return compositionData.sort((a, b) => (b.value ?? 0) - (a.value ?? 0));
    }
    return compositionData;
  })();

  return { type: DataType.SINGLE, data: sortedCompositionData };
};
